import { useUpdateActionItem } from "@portal/api/action-item/admin";
import { AssigneeAvatars } from "@portal/components/features/assignees";
import { cn } from "@portal/components/lib/utils";
import { Badge } from "@portal/components/primitives";
import { CheckMarkBubble } from "@portal/components/ui/CheckMarkBubble";
import { RowActionButtons } from "@portal/components/ui/row-action-buttons/RowActionButtons";
import { useAuthenticatedUser } from "@portal/providers/AuthenticatedUserProvider";
import { ColumnDef } from "@tanstack/react-table";
import { HiChevronDown, HiChevronRight } from "react-icons/hi2";
import { formatEmpty, formatHtmlString } from "../../../../utils";
import { ActionItemWithNesting } from "./useActionItems";

export const getActionItemTableColumns = ({
	addRecentlyCompleted,
	removeRecentlyCompleted,
}: {
	addRecentlyCompleted: (ids: string[]) => void;
	removeRecentlyCompleted: (ids: string[]) => void;
}): ColumnDef<ActionItemWithNesting>[] => [
	{
		id: "open",
		accessorKey: "open",
		header: "Open",
		cell: ({ row }) => {
			const { toggleCompleted } = useUpdateActionItem();
			const { user } = useAuthenticatedUser();
			const isChildRow = row.depth > 0;

			return row.getCanExpand() ? (
				<div className="flex items-center space-x-2">
					<RowActionButtons alwaysShow>
						<button onClick={row.getToggleExpandedHandler()}>
							{row.getIsExpanded() ? <HiChevronDown /> : <HiChevronRight />}
						</button>
					</RowActionButtons>
					<Badge
						count={row.original.numOpenItems}
						size="sm"
						tone="neutral"
						placement="inline"
						className="w-[25px]"
					/>
				</div>
			) : (
				<div className={cn(isChildRow ? "ml-[26px]" : "")}>
					<RowActionButtons alwaysShow>
						<CheckMarkBubble
							toggleComplete={() => {
								// If marking complete, add to recently completed
								if (row.original.status !== "complete") {
									// Add to the map to keep it in view while we mark it complete
									addRecentlyCompleted([row.original.id]);
									toggleCompleted(row.original, user.id);
								} else {
									// Toggle back to incomplete before removing from the map to keep it in view
									toggleCompleted(row.original, user.id);
									// If marking incomplete, remove from recently completed
									removeRecentlyCompleted([row.original.id]);
								}
							}}
							isComplete={row.original.status === "complete"}
						/>
					</RowActionButtons>
				</div>
			);
		},
		size: 80,
		enableResizing: false,
	},
	{
		id: "dueAt",
		accessorKey: "dueAt",
		header: "Due Date",
		cell: ({ row }) => {
			const dueDate = row.original.dueAt?.toFormat("LLL dd, yyyy");

			return <span title={dueDate}>{dueDate}</span>;
		},
		size: 125,
		enableResizing: false,
		enableSorting: true,
	},
	{
		id: "Entity",
		accessorKey: "entityTitle",
		header: "Entity",
		cell: ({ row }) => {
			const isChildRow = row.depth > 0;
			const entityTitle = formatEmpty(row.original.entityTitle);
			return isChildRow ? (
				<div className="opacity-50">{entityTitle}</div>
			) : (
				<span title={entityTitle}>{entityTitle}</span>
			);
		},
		minSize: 245,
		size: 245,
		enableResizing: true,
		enableSorting: true,
		enableColumnFilter: true,
	},
	{
		id: "Description",
		accessorKey: "title",
		header: "Description",
		cell: ({ row }) => {
			const description = formatEmpty(row.original.title, "");

			return <span title={description}>{description}</span>;
		},
		minSize: 150,
		size: 325,
		enableResizing: true,
		enableSorting: true,
		enableColumnFilter: true,
	},
	{
		id: "Notes",
		accessorKey: "content",
		header: "Notes",
		cell: ({ row }) => {
			const content = row.original.content ? formatHtmlString(row.original.content) : "";
			return <span title={content}>{content}</span>;
		},
		minSize: 350,
		size: 500,
		enableResizing: true,
		enableSorting: true,
		enableColumnFilter: true,
	},
	{
		id: "assignee",
		accessorKey: "assigneeId",
		header: "Assignee",
		cell: (props) => {
			const { assigneeId, assigneeIds } = props.row.original;
			const assignees = assigneeIds ?? [assigneeId];

			return (
				<RowActionButtons alwaysShow>
					<AssigneeAvatars assigneeIds={assignees} />
				</RowActionButtons>
			);
		},
		meta: {
			noTruncate: true,
		},
		minSize: 90,
		size: 90,
		enableResizing: false,
		enableSorting: true,
		enableGlobalFilter: false,
		enableHiding: false,
		enableColumnFilter: true,
		enablePinning: false,
		enableGrouping: false,
		enableMultiSort: false,
	},
];
